/**
 * 商品卡片 Label 展示逻辑说明（与产品文档同步维护）
 * @see https://sayweee.atlassian.net/wiki/spaces/TZ/pages/1593540650/Label
 *
 * 文档涵盖：标签类型（label_list / week_featured、off 等）、Rank / Feature / 常规 / 状态覆盖分类，
 * 以及 `onlyShowFeatureLabel`、`preferFeatureLabel`、`isLightningDeals`、`showBigOffLabel` 等与 PC/H5 差异（布局、大折扣勋章等）。
 *
 */
import { StatusEnum } from '@/types/cart';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { isValidArray } from '@/utils/isValidArray';
import { LabelItemProps } from '@/types/product';
import ProductLabelItem from './label-item';
import { ProductLabelKeyEnum } from '../../constants';
import { ProductCardProps } from '../../types';
import ProductBigOffLabel from './big-off-label';

const getLightningLabels = (list: LabelItemProps[], otherLabels: LabelItemProps[]) => {
  const firstLabel = list[0];
  if (firstLabel?.label_key === ProductLabelKeyEnum.OFF) {
    return list.filter(item => item.label_key === ProductLabelKeyEnum.OFF);
  }
  return isValidArray(otherLabels) ? [otherLabels[0]] : [];
};

const checkHasBigOff = (list: LabelItemProps[], showBigOffLabel: boolean, discount_percentage?: string | number) => {
  if (!showBigOffLabel || !discount_percentage) return false;
  return list.some(item => item.label_key === ProductLabelKeyEnum.OFF);
};

const ProductLabel: React.FC<Partial<
  ProductCardProps & {
    labelNode?: React.ReactNode | (() => React.ReactNode);
    headerContainerCls?: string;
    exactTargetPos?: number;
    isLightningDeals?: boolean;
    showBigOffLabel?: boolean;
  }
>> = props => {
  const { t } = useTranslation('common');

  const {
    data,
    preSell,
    isLightningDeals,
    labelProps = {},
    rankTag,
    labelHeadCls,
    showBigOffLabel = false,
    labelOffType
  } = props;

  const {
    showAllLabel = false,
    showFeatureLabel = true,
    onlyShowFeatureLabel = false,
    preferFeatureLabel = false,
    showBorder = true
  } = labelProps;

  const { label_list = [], is_presale, sold_status, discount_percentage } = data || {};

  const finalLabels = useMemo(() => {
    // 1. 基础分类与预判 (一次遍历完成)
    const featureLabels: LabelItemProps[] = [];
    const otherLabels: LabelItemProps[] = [];

    label_list.forEach(item => {
      if (item.label_key === ProductLabelKeyEnum.WEEK_FEATURED) {
        featureLabels.push(item);
      } else {
        otherLabels.push(item);
      }
    });

    // 2. 快捷出口：仅展示 Feature
    if (onlyShowFeatureLabel || (isValidArray(featureLabels) && preferFeatureLabel)) return featureLabels;

    // 3. 快捷出口：硬编码的预售覆盖 (preSell 为 true 且不是原生 is_presale)
    if (preSell && !is_presale) {
      return [
        {
          label_key: ProductLabelKeyEnum.PRESALE,
          label_name: t('pre_sell'),
          label_color: '#820b6a'
        }
      ] as LabelItemProps[];
    }

    // 4. 预售，售罄不展示label
    const isSoldOut = sold_status === StatusEnum?.sold_out;
    if (is_presale && isSoldOut) {
      return [];
    }

    // 使用简单的 Switch 或逻辑块处理策略
    if (isLightningDeals) {
      return getLightningLabels(label_list, otherLabels);
    }

    // 5. 应用展示规则 (Quantity & Priority)
    let result = showAllLabel ? [...otherLabels] : otherLabels.slice(0, 1);

    // 大折扣标签,如果存在大折扣标签，则不展示featured标签
    if (checkHasBigOff(result, showBigOffLabel, discount_percentage)) {
      return result;
    }

    // 追加 Feature 标签
    if (showFeatureLabel && isValidArray(featureLabels)) {
      result = [...result, ...featureLabels];
    }

    return result;
  }, [
    label_list,
    onlyShowFeatureLabel,
    preferFeatureLabel,
    preSell,
    is_presale,
    sold_status,
    isLightningDeals,
    showAllLabel,
    showBigOffLabel,
    discount_percentage,
    showFeatureLabel,
    t
  ]);

  if (!isValidArray(finalLabels) && !rankTag) return null;

  return (
    <div className={twMerge('flex flex-col items-start overflow-hidden', labelHeadCls)}>
      {rankTag}
      {finalLabels?.map((label, i) => {
        const isBigOffItem = showBigOffLabel && label.label_key === 'off' && discount_percentage;
        if (isBigOffItem) {
          return (
            <ProductBigOffLabel
              key={`${label.label_key}-${i}`}
              labelItem={label}
              discount_percentage={discount_percentage}
              labelOffType={labelOffType}
            />
          );
        }
        return (
          <ProductLabelItem
            key={`${label.label_key}-${i}`}
            label={label}
            showBorder={showBorder}
            hasRankTag={!!rankTag}
          />
        );
      })}
    </div>
  );
};

export default ProductLabel;
